import type {
  ScoutDashboardMetricsQuery,
  ScoutDashboardMetricsQueryVariables,
} from "generated-graphql/graphql";

import { useQuery } from "@apollo/client";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Alert,
  Box,
  ButtonBase,
  Collapse,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { CommandButton } from "components/CommandButton";
import ScoutIcon from "components/ScoutIcon";
import { gql } from "generated-graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useVisibility } from "hooks/useVisibility";
import { type Dispatch, useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { prettyPrintDateTime } from "util/dates";

import {
  DEFAULT_SCOUT_DASHBOARD_SECTION_SETTINGS,
  ManageScoutChartsPopover,
  createDefaultScoutDashboardSectionConfig,
  deleteScoutDashboardSectionConfig,
  getScoutDashboardChartStorageKey,
  getVisibleScoutDashboardSectionItems,
  normalizeScoutDashboardSectionSettings,
  saveScoutDashboardSectionConfig,
  selectScoutDashboardSectionConfig,
  setCurrentScoutDashboardSectionConfig,
  type ScoutDashboardSectionConfig,
  type ScoutDashboardSectionSettings,
} from "./ScoutDashboardManageCharts";
import {
  createInitialScoutDashboardState,
  getScoutDashboardRangeIdentity,
  reduceScoutDashboardRangeState,
  selectScoutDashboardRenderState,
  shouldUpdateScoutDashboardUrlOnMetricsCompleted,
  type ScoutDashboardAction,
  type ScoutDashboardRangeKey,
  type ScoutDashboardRangeState,
} from "./scoutDashboardRangeState";
import { ScoutDashboardStatCard } from "./ScoutDashboardStatCard";
import { ScoutDashboardTimeSeriesCard } from "./ScoutDashboardTimeSeriesCard";

const SCOUT_DASHBOARD_METRICS_QUERY = gql(`
  query ScoutDashboardMetrics($input: ScoutDashboardMetricsInput!) {
    scoutDashboardMetrics(input: $input) {
      startTime
      endTime
      periodSeconds
      scope
      statWidgets {
        id
        title
        kind
        unit
        documentation {
          badges
          details
        }
        yAxisMin
        yAxisMax
        referenceLines {
          value
          label
          color
        }
        series {
          id
          label
          unit
          points {
            timestamp
            value
          }
        }
      }
      chartSections {
        id
        title
        widgets {
          id
          title
          kind
          unit
          documentation {
            badges
            details
          }
          yAxisMin
          yAxisMax
          referenceLines {
            value
            label
            color
          }
          series {
            id
            label
            unit
            points {
              timestamp
              value
            }
          }
        }
      }
    }
  }
`);

type RangeOption = {
  key: ScoutDashboardRangeKey;
  label: string;
  durationMs: number;
  periodSeconds: number;
};
type ScoutDashboardSectionExpansionState = Record<string, boolean>;
type ScoutDashboardWidget =
  ScoutDashboardMetricsQuery["scoutDashboardMetrics"]["statWidgets"][number];
type ScoutDashboardChartSection =
  ScoutDashboardMetricsQuery["scoutDashboardMetrics"]["chartSections"][number];
const EMPTY_SCOUT_DASHBOARD_WIDGETS: ScoutDashboardWidget[] = [];
const EMPTY_SCOUT_DASHBOARD_CHART_SECTIONS: ScoutDashboardChartSection[] = [];
const STAT_WIDGET_SKELETON_COUNT = 8;
const EMPTY_STAT_WIDGET_PLACEHOLDERS: (ScoutDashboardWidget | null)[] = Array.from(
  { length: STAT_WIDGET_SKELETON_COUNT },
  () => null
);

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const DASHBOARD_REFRESH_INTERVAL_MS = 60 * 1000;

/**
 * Keep the selectable range at one week or less. The API uses CloudWatch SEARCH
 * for dynamic node/task metrics, and SEARCH only discovers metrics that have
 * reported in the past two weeks; wider ranges also increase GetMetricData
 * datapoint volume across every discovered dynamic series.
 */
const RANGE_OPTIONS = [
  { key: "1h", label: "1Hr", durationMs: HOUR_MS, periodSeconds: 60 },
  { key: "3h", label: "3Hr", durationMs: 3 * HOUR_MS, periodSeconds: 360 },
  { key: "12h", label: "12Hr", durationMs: 12 * HOUR_MS, periodSeconds: 1500 },
  { key: "1d", label: "1d", durationMs: DAY_MS, periodSeconds: 3600 },
  { key: "3d", label: "3d", durationMs: 3 * DAY_MS, periodSeconds: 10800 },
  { key: "1w", label: "1w", durationMs: 7 * DAY_MS, periodSeconds: 24000 },
] satisfies RangeOption[];

function readRangeKey(value: string | null): ScoutDashboardRangeKey {
  if (value === "1h") {
    return "1h";
  }
  if (value === "3h") {
    return "3h";
  }
  if (value === "12h") {
    return "12h";
  }
  if (value === "1d") {
    return "1d";
  }
  if (value === "3d") {
    return "3d";
  }
  if (value === "1w") {
    return "1w";
  }
  return "1h";
}

function getRangeOption(key: ScoutDashboardRangeKey): RangeOption {
  const option = RANGE_OPTIONS.find((candidate) => candidate.key === key);
  return option ?? RANGE_OPTIONS[0];
}

function getRangeState(params: URLSearchParams, now: Date): ScoutDashboardRangeState {
  const rangeKey = readRangeKey(params.get("range"));
  const rangeOption = getRangeOption(rangeKey);
  const durationMs = rangeOption.durationMs;
  const endTime = now;
  const startTime = new Date(endTime.getTime() - durationMs);

  return {
    rangeKey,
    startTime,
    endTime,
    periodSeconds: rangeOption.periodSeconds,
  };
}

function makeSearchParams(input: { rangeKey: ScoutDashboardRangeKey }): URLSearchParams {
  const next = new URLSearchParams();
  next.set("range", input.rangeKey);
  return next;
}

function getScoutDashboardSectionExpansionStorageKey(userEmail?: string | null): string {
  return `scout-dashboard-section-expansion|${userEmail ?? "anonymous"}`;
}

/**
 * Owns dashboard refresh effects without closing over query results or widget-derived values.
 * React keeps the cleanup function returned from the interval effect until that effect is
 * replaced or unmounted. If the effect is created inside the full dashboard render scope, the
 * cleanup closure can keep that render's `scoutDashboardMetrics` result and derived widget arrays
 * reachable even after newer data arrives. This hook only closes over polling state, so the saved
 * interval cleanup cannot retain stale dashboard payloads.
 */
function useScoutDashboardPolling({
  isManualRangeLoading,
  displayRangeKey,
  dispatchDashboardAction,
}: {
  isManualRangeLoading: boolean;
  displayRangeKey: ScoutDashboardRangeKey;
  dispatchDashboardAction: Dispatch<ScoutDashboardAction>;
}) {
  const requestDashboardPoll = useCallback(() => {
    dispatchDashboardAction({
      type: "requestPoll",
      rangeState: getRangeState(makeSearchParams({ rangeKey: displayRangeKey }), new Date()),
    });
  }, [dispatchDashboardAction, displayRangeKey]);

  const handleVisibilityChange = useCallback(
    (nextVisibilityState: DocumentVisibilityState) => {
      if (nextVisibilityState === "visible" && !isManualRangeLoading) {
        requestDashboardPoll();
      }
    },
    [isManualRangeLoading, requestDashboardPoll]
  );
  const visibilityState = useVisibility(handleVisibilityChange);
  const isVisible = visibilityState === "visible";

  useEffect(() => {
    if (isManualRangeLoading || !isVisible) {
      return undefined;
    }

    const intervalId = window.setInterval(requestDashboardPoll, DASHBOARD_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [isManualRangeLoading, isVisible, requestDashboardPoll]);
}

/**
 * Renders no UI; it gives polling a separate React component scope from the dashboard render that
 * computes chart and stat widgets. That component boundary means the interval effect cleanup is
 * created from this tiny poller render, not from the parent render that has `data`, `previousData`,
 * `dashboardData`, and chart arrays in scope.
 */
function ScoutDashboardPoller({
  isManualRangeLoading,
  displayRangeKey,
  dispatchDashboardAction,
}: {
  isManualRangeLoading: boolean;
  displayRangeKey: ScoutDashboardRangeKey;
  dispatchDashboardAction: Dispatch<ScoutDashboardAction>;
}) {
  useScoutDashboardPolling({ isManualRangeLoading, displayRangeKey, dispatchDashboardAction });
  return null;
}

export function ScoutDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useCurrentUser();
  const [dashboardState, dispatchDashboardAction] = useReducer(
    reduceScoutDashboardRangeState,
    searchParams,
    (initialSearchParams) =>
      createInitialScoutDashboardState(getRangeState(initialSearchParams, new Date()))
  );
  const [manageChartsAnchorEl, setManageChartsAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { displayRangeState, requestedRangeState } = dashboardState;
  const chartSettingsStorageKey = getScoutDashboardChartStorageKey(currentUser.user?.email);
  const sectionExpansionStorageKey = getScoutDashboardSectionExpansionStorageKey(
    currentUser.user?.email
  );
  const [sectionSettings, setSectionSettings] = useLocalStorageState<ScoutDashboardSectionSettings>(
    chartSettingsStorageKey,
    { defaultValue: DEFAULT_SCOUT_DASHBOARD_SECTION_SETTINGS }
  );
  const [sectionExpansionState, setSectionExpansionState] =
    useLocalStorageState<ScoutDashboardSectionExpansionState>(sectionExpansionStorageKey, {
      defaultValue: {},
    });
  const queryVariables = useMemo<ScoutDashboardMetricsQueryVariables>(
    () => ({
      input: {
        startTime: requestedRangeState.startTime.toISOString(),
        endTime: requestedRangeState.endTime.toISOString(),
        periodSeconds: requestedRangeState.periodSeconds,
      },
    }),
    [requestedRangeState.endTime, requestedRangeState.periodSeconds, requestedRangeState.startTime]
  );

  /**
   * Apollo completion is the event boundary where a query result can be matched to the requested
   * range and committed atomically. Doing this here keeps URL updates, error recovery, and chart
   * data changes synchronized without a follow-up effect that can run after an intermediate render.
   */
  const handleDashboardMetricsCompleted = useCallback(
    (result: ScoutDashboardMetricsQuery) => {
      const metrics = result.scoutDashboardMetrics;
      if (shouldUpdateScoutDashboardUrlOnMetricsCompleted(dashboardState, metrics)) {
        setSearchParams(
          makeSearchParams({ rangeKey: dashboardState.requestedRangeState.rangeKey })
        );
      }

      dispatchDashboardAction({ type: "metricsCompleted", metrics });
    },
    [dashboardState, setSearchParams]
  );

  const handleDashboardMetricsError = useCallback(() => {
    dispatchDashboardAction({ type: "metricsErrored" });
  }, []);

  const { data, loading, error } = useQuery<
    ScoutDashboardMetricsQuery,
    ScoutDashboardMetricsQueryVariables
  >(SCOUT_DASHBOARD_METRICS_QUERY, {
    variables: queryVariables,
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    onCompleted: handleDashboardMetricsCompleted,
    onError: handleDashboardMetricsError,
  });

  const isManualRangeLoading = dashboardState.requestSource === "manual" && loading;

  const queryDashboardMetrics = data?.scoutDashboardMetrics;
  const dashboardRenderState = useMemo(
    () =>
      selectScoutDashboardRenderState({
        committedState: dashboardState.committedState,
        metrics: queryDashboardMetrics,
        requestedRangeState,
      }),
    [dashboardState.committedState, queryDashboardMetrics, requestedRangeState]
  );
  const dashboardMetrics = dashboardRenderState?.metrics;
  const dashboardRangeState = dashboardRenderState?.rangeState ?? displayRangeState;
  const dashboardRangeIdentity = getScoutDashboardRangeIdentity(dashboardRangeState);
  const statWidgets = dashboardMetrics?.statWidgets ?? EMPTY_SCOUT_DASHBOARD_WIDGETS;
  const chartSections = dashboardMetrics?.chartSections ?? EMPTY_SCOUT_DASHBOARD_CHART_SECTIONS;
  const reservedChartSeriesCount = useMemo(
    () =>
      chartSections.reduce(
        (maxSeriesCount, section) =>
          Math.max(maxSeriesCount, ...section.widgets.map((widget) => widget.series.length)),
        0
      ),
    [chartSections]
  );
  const sectionOptions = useMemo(
    () =>
      chartSections.map((section) => ({
        id: section.id,
        title: section.title,
        charts: section.widgets.map((widget) => ({
          id: widget.id,
          title: widget.title,
        })),
      })),
    [chartSections]
  );
  const normalizedSectionSettings = useMemo(
    () => normalizeScoutDashboardSectionSettings(sectionSettings, sectionOptions),
    [sectionOptions, sectionSettings]
  );
  const visibleChartSections = useMemo(
    () => getVisibleScoutDashboardSectionItems(chartSections, normalizedSectionSettings.current),
    [chartSections, normalizedSectionSettings]
  );
  const showManageChartsPopover = Boolean(manageChartsAnchorEl);
  const isInitialLoading = loading && !dashboardRenderState;
  const hasDashboardError = Boolean(error) || dashboardState.hasManualRangeError;
  const canShowWidgets = !hasDashboardError || Boolean(dashboardRenderState);
  const showEmptyState =
    canShowWidgets &&
    !hasDashboardError &&
    !isInitialLoading &&
    statWidgets.length === 0 &&
    chartSections.length === 0;
  const rangeDurationMs =
    dashboardRangeState.endTime.getTime() - dashboardRangeState.startTime.getTime();

  const updateRange = (nextRangeKey: ScoutDashboardRangeKey) => {
    dispatchDashboardAction({
      type: "requestManualRange",
      rangeState: getRangeState(makeSearchParams({ rangeKey: nextRangeKey }), new Date()),
    });
  };

  const handleCurrentSectionConfigChange = useCallback(
    (config: ScoutDashboardSectionConfig) => {
      setSectionSettings((previousSettings) =>
        setCurrentScoutDashboardSectionConfig(previousSettings, config, sectionOptions)
      );
    },
    [sectionOptions, setSectionSettings]
  );

  const handleSaveSectionConfig = useCallback(
    (name: string) => {
      setSectionSettings((previousSettings) =>
        saveScoutDashboardSectionConfig(previousSettings, name, sectionOptions)
      );
    },
    [sectionOptions, setSectionSettings]
  );

  const handleDeleteSectionConfig = useCallback(
    (name: string) => {
      setSectionSettings((previousSettings) =>
        deleteScoutDashboardSectionConfig(previousSettings, name, sectionOptions)
      );
    },
    [sectionOptions, setSectionSettings]
  );

  const handleSelectSectionConfig = useCallback(
    (name: string) => {
      setSectionSettings((previousSettings) =>
        selectScoutDashboardSectionConfig(previousSettings, name, sectionOptions)
      );
    },
    [sectionOptions, setSectionSettings]
  );

  const handleResetSectionConfig = useCallback(() => {
    setSectionSettings((previousSettings) =>
      setCurrentScoutDashboardSectionConfig(
        previousSettings,
        createDefaultScoutDashboardSectionConfig(sectionOptions.map((section) => section.id)),
        sectionOptions
      )
    );
  }, [sectionOptions, setSectionSettings]);

  const handleToggleSectionExpansion = useCallback(
    (sectionId: string) => {
      setSectionExpansionState((previousState) => ({
        ...previousState,
        [sectionId]: !(previousState[sectionId] ?? true),
      }));
    },
    [setSectionExpansionState]
  );

  return (
    <Stack spacing={3} sx={{ p: { xs: 2, md: 3 } }}>
      <ScoutDashboardPoller
        isManualRangeLoading={isManualRangeLoading}
        displayRangeKey={displayRangeState.rangeKey}
        dispatchDashboardAction={dispatchDashboardAction}
      />
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        spacing={2}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} minWidth={0}>
          <ScoutIcon sx={{ width: 36, height: 36 }} />
          <Box minWidth={0}>
            <Typography variant="h4" fontWeight={600}>
              Scout Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {prettyPrintDateTime(dashboardRangeState.startTime.toISOString())} to{" "}
              {prettyPrintDateTime(dashboardRangeState.endTime.toISOString())}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
          <CommandButton
            disabled={sectionOptions.length === 0}
            onClick={(event) => setManageChartsAnchorEl(event.currentTarget)}
          >
            Manage Charts
          </CommandButton>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={
              isManualRangeLoading ? requestedRangeState.rangeKey : dashboardRangeState.rangeKey
            }
            onChange={(_event, value: ScoutDashboardRangeKey | null) => {
              if (value) {
                updateRange(value);
              }
            }}
            aria-label="Scout dashboard time range"
          >
            {RANGE_OPTIONS.map((option) => (
              <ToggleButton
                key={option.key}
                value={option.key}
                aria-label={option.label}
                disabled={isManualRangeLoading && option.key !== requestedRangeState.rangeKey}
              >
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {showManageChartsPopover && (
            <ManageScoutChartsPopover
              open={showManageChartsPopover}
              anchorEl={manageChartsAnchorEl}
              onClose={() => setManageChartsAnchorEl(null)}
              sections={sectionOptions}
              currentConfig={normalizedSectionSettings.current}
              savedConfigs={normalizedSectionSettings.savedConfigs}
              activeConfigName={normalizedSectionSettings.activeConfigName}
              onCurrentConfigChange={handleCurrentSectionConfigChange}
              onSaveConfig={handleSaveSectionConfig}
              onDeleteConfig={handleDeleteSectionConfig}
              onSelectConfig={handleSelectSectionConfig}
              onResetConfig={handleResetSectionConfig}
            />
          )}
        </Stack>
      </Stack>

      {hasDashboardError && <Alert severity="error">Failed to load Scout dashboard metrics.</Alert>}
      {showEmptyState && <Alert severity="info">No Scout dashboard widgets returned.</Alert>}

      {canShowWidgets && (
        <Box
          aria-busy={isManualRangeLoading}
          data-testid="scout-dashboard-widget-view"
          sx={{
            opacity: isManualRangeLoading ? 0.4 : 1,
            transition: "opacity 150ms ease-in-out",
          }}
        >
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {(isInitialLoading ? EMPTY_STAT_WIDGET_PLACEHOLDERS : statWidgets).map(
              (widget, index) => (
                <Grid key={widget?.id ?? index} size={{ xs: 12, sm: 6, lg: 3 }}>
                  <ScoutDashboardStatCard
                    widget={widget ?? undefined}
                    loading={isInitialLoading}
                    periodSeconds={dashboardMetrics?.periodSeconds ?? 60}
                  />
                </Grid>
              )
            )}
          </Grid>

          <Stack spacing={3}>
            {isInitialLoading && (
              <Grid container spacing={2}>
                {Array.from({ length: 12 }).map((_item, index) => (
                  <Grid key={`skeleton-${index}`} size={{ xs: 12, lg: 6 }}>
                    <ScoutDashboardTimeSeriesCard
                      loading={true}
                      rangeDurationMs={rangeDurationMs}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
            {!isInitialLoading &&
              visibleChartSections.map((section) => {
                const isExpanded = sectionExpansionState[section.id] ?? true;
                const collapseId = `scout-dashboard-section-${section.id}`;

                return (
                  <Box key={section.id}>
                    <ButtonBase
                      aria-controls={collapseId}
                      aria-expanded={isExpanded}
                      onClick={() => handleToggleSectionExpansion(section.id)}
                      sx={{
                        alignItems: "center",
                        borderRadius: 1,
                        display: "flex",
                        justifyContent: "flex-start",
                        mb: 1.5,
                        px: 0.5,
                        py: 0.5,
                        textAlign: "left",
                        width: "100%",
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <ChevronRightIcon
                        sx={{
                          color: "text.secondary",
                          mr: 0.5,
                          transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                          transition: "transform 0.2s ease-in-out",
                        }}
                      />
                      <Typography variant="h6" fontWeight={600}>
                        {section.title}
                      </Typography>
                    </ButtonBase>
                    <Collapse id={collapseId} in={isExpanded} timeout="auto" unmountOnExit>
                      <Grid container spacing={2}>
                        {section.widgets.map((widget) => (
                          <Grid key={widget.id} size={{ xs: 12, lg: 6 }}>
                            <ScoutDashboardTimeSeriesCard
                              reservedSeriesCount={reservedChartSeriesCount}
                              widget={widget}
                              loading={false}
                              chartRangeKey={dashboardRangeIdentity}
                              rangeDurationMs={rangeDurationMs}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Collapse>
                  </Box>
                );
              })}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
